import { useMemo } from "react";
import { useContinuousAngle } from "../hooks/useContinuousAngle";
import { angleDelta } from "../lib/geo";

export interface CompassTarget {
  id: string;
  name: string;
  /** Kuzeyden saat yönünde kerteriz. */
  bearing: number;
}

interface CompassProps {
  /** "single": tek hedefe ok. "all": tüm hedefler kadran üzerinde bant olarak. */
  mode: "single" | "all";
  /** Ekran üstünün baktığı yön; null ise kuzey yukarı modu. */
  heading: number | null;
  targets: CompassTarget[];
  /** Ekranın o an baktığı hedef; etiketi her zaman gösterilir. */
  activeId?: string | null;
  /** Kadranın ortasındaki başlık. */
  label: string;
  /** Başlığın altındaki açıklama. */
  detail: string;
  aligned: boolean;
  /** Bir hedef bandının yarı genişliği (derece). */
  halfWidthDeg: number;
}

const TICKS = Array.from({ length: 72 }, (_, i) => i * 5);
const CARDINAL_MARKS = [
  { deg: 0, text: "K" },
  { deg: 90, text: "D" },
  { deg: 180, text: "G" },
  { deg: 270, text: "B" },
];

const BAND_RADIUS = 85;
const LABEL_RADIUS = 70;
const BAND_INNER = 78;
const BAND_OUTER = 92;
/** İki etiket bu açıdan yakınsa ikincisi çizilmez — üst üste binmesinler. */
const LABEL_MIN_GAP_DEG = 22;

/** Kadran merkezine göre kutupsal noktayı SVG koordinatına çevirir. */
function point(radius: number, deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180;
  return [100 + radius * Math.sin(rad), 100 - radius * Math.cos(rad)];
}

function arcPath(radius: number, fromDeg: number, toDeg: number): string {
  const [x1, y1] = point(radius, fromDeg);
  const [x2, y2] = point(radius, toDeg);
  const large = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radius} ${radius} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

export function Compass({
  mode,
  heading,
  targets,
  activeId,
  label,
  detail,
  aligned,
  halfWidthDeg,
}: CompassProps) {
  const northUp = heading === null;
  const dialTarget = northUp ? 0 : -heading;
  const dialAngle = useContinuousAngle(dialTarget);

  const single = mode === "single" ? (targets[0] ?? null) : null;
  const needleTarget = single === null ? null : northUp ? single.bearing : single.bearing - heading;
  const needleAngle = useContinuousAngle(needleTarget);

  const ticks = useMemo(
    () =>
      TICKS.map((deg) => {
        const major = deg % 45 === 0;
        const medium = !major && deg % 15 === 0;
        return (
          <line
            key={deg}
            x1="100"
            y1={major ? 12 : medium ? 14 : 16}
            x2="100"
            y2={major ? 26 : medium ? 22 : 20}
            className={major ? "tick tick--major" : "tick"}
            transform={`rotate(${deg} 100 100)`}
          />
        );
      }),
    [],
  );

  // Yakın yönlerdeki etiketler birbirini okunmaz hâle getirdiği için
  // seyreltilir; bakılan hedefin etiketi her zaman kalır.
  const labelled = useMemo(() => {
    if (mode !== "all") return [];
    const active = targets.find((target) => target.id === activeId);
    const kept: CompassTarget[] = active ? [active] : [];
    for (const target of [...targets].sort((a, b) => a.bearing - b.bearing)) {
      if (target.id === active?.id) continue;
      const clashes = kept.some(
        (other) => Math.abs(angleDelta(other.bearing, target.bearing)) < LABEL_MIN_GAP_DEG,
      );
      if (!clashes) kept.push(target);
    }
    return kept;
  }, [mode, targets, activeId]);

  const ariaLabel =
    mode === "all"
      ? `Pusula, kayıtlı ${targets.length} konumun yönü`
      : single
        ? `${single.name} yönü, kuzeyden ${Math.round(single.bearing)} derece`
        : "Pusula, hedef hesaplanıyor";

  return (
    <div className={`compass ${aligned ? "compass--aligned" : ""}`}>
      <svg viewBox="0 0 200 200" className="compass__svg" role="img" aria-label={ariaLabel}>
        {/* Kullanıcının baktığı yönü gösteren sabit işaret */}
        <polygon points="100,2 94,14 106,14" className="compass__cursor" />

        <g className="compass__dial" style={{ transform: `rotate(${dialAngle}deg)` }}>
          <circle cx="100" cy="100" r="94" className="compass__ring" />
          {mode === "all" ? (
            <>
              {/* Boş yönler yeşil, kayıtlı konumların yönü kırmızı. */}
              <circle
                cx="100"
                cy="100"
                r={BAND_RADIUS}
                className="compass__band compass__band--free"
              />
              {targets.map((target) => (
                <path
                  key={target.id}
                  d={arcPath(
                    BAND_RADIUS,
                    target.bearing - halfWidthDeg,
                    target.bearing + halfWidthDeg,
                  )}
                  className="compass__band compass__band--target"
                />
              ))}
              {/* Bantlar birleştiğinde tek tek yönleri ayırt ettiren çizgi. */}
              {targets.map((target) => {
                const [x1, y1] = point(BAND_INNER, target.bearing);
                const [x2, y2] = point(BAND_OUTER, target.bearing);
                return (
                  <line
                    key={`mark-${target.id}`}
                    x1={x1.toFixed(2)}
                    y1={y1.toFixed(2)}
                    x2={x2.toFixed(2)}
                    y2={y2.toFixed(2)}
                    className="compass__band-mark"
                  />
                );
              })}
            </>
          ) : (
            <circle
              cx="100"
              cy="100"
              r="78"
              className="compass__ring compass__ring--inner"
            />
          )}
          {ticks}
          {CARDINAL_MARKS.map(({ deg, text }) => (
            <g key={text} transform={`rotate(${deg} 100 100)`}>
              {/* Harfler kadranla dönmez; ekrana göre hep dik durur. */}
              <text
                x="100"
                y="42"
                className={`compass__cardinal ${deg === 0 ? "compass__cardinal--north" : ""}`}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${-deg - dialAngle} 100 42)`}
              >
                {text}
              </text>
            </g>
          ))}
          {mode === "all" &&
            labelled.map((target) => {
              const [x, y] = point(LABEL_RADIUS, target.bearing);
              return (
                <text
                  key={target.id}
                  x={x.toFixed(2)}
                  y={y.toFixed(2)}
                  className="compass__pin"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${-dialAngle} ${x.toFixed(2)} ${y.toFixed(2)})`}
                >
                  {target.name}
                </text>
              );
            })}
        </g>

        {mode === "single" && single && (
          <g className="compass__needle" style={{ transform: `rotate(${needleAngle}deg)` }}>
            <line x1="100" y1="66" x2="100" y2="42" className="compass__needle-line" />
            <polygon points="100,14 88,44 112,44" className="compass__needle-head" />
          </g>
        )}
      </svg>

      <div className="compass__center">
        <span className="compass__label">{label}</span>
        <span className="compass__offset">{detail}</span>
      </div>
    </div>
  );
}
